// Copyright ©️ 2026 eVolve MEP, LLC

import type { WithOptional } from '../_helpers/helper-types';

import type { ISOTimestamp } from '../Misc/dateOnly';
import type { ShipmentId } from '../Shipment/types';
import type { TaskTypeId } from '../ShopSetup/types';
import type { TaskId } from '../Tasks/types';
import type { UserId } from '../User/types';
import type { WorkOrderId, WorkOrderItemId } from '../WorkOrder/types';

declare const fileIdSymbol: unique symbol;
export type FileId = string & { [fileIdSymbol]: true };

declare const rowIdSymbol: unique symbol;
type RowId = string & { [rowIdSymbol]: never };

declare const fileMarkupIdSymbol: unique symbol;
export type FileMarkupId = string & { [fileMarkupIdSymbol]: true };

export type MarkupPage = { page: number; markup: string };

export type FileMarkup = {
  fileMarkupId: FileMarkupId;
  markupPages: MarkupPage[];
};

export type UpdateMarkupBody = {
  /** The page number of the file the markup applies to. */
  page: number;
  /** The user-generated SVG+XML markup, stored as a string. */
  markup: string;
};

/** Entity types that own their attachments directly and support uploading and deleting them. */
export type UploadableEntityAttachmentType = 'workOrder' | 'taskType' | 'task' | 'workOrderItem' | 'shipment';

/**
 * Read-only "roll-up" section types that aggregate a child entity's attachments onto a parent
 * (e.g. all of a work order's task attachments). No upload or delete routes exist for these;
 * uploads and deletes go through the child entity directly.
 */
export type RollupEntityAttachmentType = 'workOrderBop' | 'workOrderItems';

export type EntityAttachmentType = UploadableEntityAttachmentType | RollupEntityAttachmentType;
export type EntityAttachmentId<T extends EntityAttachmentType> = T extends
  | 'workOrder'
  | 'workOrderBop'
  | 'workOrderItems'
  ? WorkOrderId
  : T extends 'taskType'
    ? TaskTypeId
    : T extends 'task'
      ? TaskId
      : T extends 'workOrderItem'
        ? WorkOrderItemId
        : T extends 'shipment'
          ? ShipmentId
          : never;

/**
 * The URL segment for each uploadable entity type's attachment routes, e.g.
 * `shop/{segment}/{entityId}/fileAttachment`. Most segments match the entity type name, but
 * work order item routes are nested under the work order routes at `shop/workOrder/item/...`.
 * Roll-up types have no entry — they are read-only aggregations with no upload or delete routes.
 */
export const entityAttachmentUrlSegments = {
  workOrder: 'workOrder',
  task: 'task',
  taskType: 'taskType',
  workOrderItem: 'workOrder/item',
  shipment: 'shipment',
} as const satisfies {
  [K in UploadableEntityAttachmentType]: string;
};

export type EntityAttachmentUrlSegment<T extends UploadableEntityAttachmentType> =
  (typeof entityAttachmentUrlSegments)[T];

export const isUploadableEntityAttachmentType = (
  entityType: EntityAttachmentType,
): entityType is UploadableEntityAttachmentType => entityType in entityAttachmentUrlSegments;

/** Builds the route for an uploadable entity's attachment endpoints. */
export const getEntityAttachmentRoute = <
  T extends UploadableEntityAttachmentType,
  S extends
    // for GETs
    | 'attachment'
    // for adding new files
    | 'fileAttachment'
    // for adding new web links
    | 'webLinkAttachment',
>(
  entityType: T,
  entityId: EntityAttachmentId<T>,
  suffix: S,
) =>
  // The template's own type is the segment×id cross-product, which includes mismatched pairs;
  // assert the per-entity correlated route union instead
  `shop/${entityAttachmentUrlSegments[entityType]}/${entityId}/${suffix}` as {
    [K in UploadableEntityAttachmentType]: `shop/${EntityAttachmentUrlSegment<K>}/${EntityAttachmentId<K>}/${S}`;
  }[T];

declare const fileAttachmentIdSymbol: unique symbol;
export type FileAttachmentId = string & { [fileAttachmentIdSymbol]: never };

declare const webLinkAttachmentIdSymbol: unique symbol;
export type WebLinkAttachmentId = string & { [webLinkAttachmentIdSymbol]: never };

export type WebLink = {
  url: string;
  name: string;
};

export type CreateWebLinkBody = WithOptional<WebLink, 'name'>;

export type AttachmentFile = {
  fileId: FileId;
  fileName: string;
  fileMarkup?: FileMarkup;
};

export type EntityAttachment<T extends EntityAttachmentType> = {
  rowId: RowId;
  createdBy: UserId;
  createdOn: ISOTimestamp;
} & (
  | ({
      [K in `${T}FileAttachmentId`]: FileAttachmentId;
    } & {
      file: AttachmentFile;
    })
  | ({
      [K in `${T}WebLinkAttachmentId`]: WebLinkAttachmentId;
    } & {
      webLink: WebLink;
    })
);

export type GetPresignedUrlResponse = {
  fileId: FileId;
  fileName: string;
  fileMarkup?: FileMarkup;
  contentType: string;
  expires: ISOTimestamp;
} & (
  | {
      preSignedUrl: string;
      error?: never;
    }
  | {
      preSignedUrl?: never;
      error: string;
    }
);

export type RevitDownloadResponse = Pick<GetPresignedUrlResponse, 'preSignedUrl' | 'expires' | 'error'> & {
  storagePath: string;
};

export type UploadableFile = {
  fileId: FileId;
  fileName: string;
  /** If populated, the FE is expected to upload the new file to S3 via the `presignedUrl` */
  preSignedUrl?: string;
  contentType?: string;
  expires?: ISOTimestamp;
};

export type EntityCanHandleUploads = {
  /** When populated, the caller is expected to PUT the files into S3 */
  files?: {
    file: UploadableFile;
  }[];
  /** Only populated when Web Links were added to the entity as part of this call */
  webLinks?: {
    webLink: {
      url: null;
      name: null;
    };
  }[];
};
